import {
  BlockType,
  ColorSchemeSchema,
  FieldGroupSchema,
  FieldRole,
  ImageFieldSchema,
  RichTextFieldSchema,
} from './Types';
import { z } from 'zod';

export enum CardIconMode {
  Icon = 'icon',
  Index = 'index',
  None = 'none',
}

export const CardIconModeSchema = z.nativeEnum(CardIconMode);

export const CardStyleSchema = z.object({
  backgroundColor: z.string(),
});

export type CardStyle = z.infer<typeof CardStyleSchema>;

export enum ValuePropLayout {
  ThreeColumn = 'ThreeColumn',
}

export const ValuePropLayoutSchema = z.nativeEnum(ValuePropLayout);

export const ValuePropHeaderFieldSchema = RichTextFieldSchema.extend({
  name: z.literal('header'),
  role: z.literal(FieldRole.Header),
});

export const ValuePropSubheaderFieldSchema = RichTextFieldSchema.extend({
  name: z.literal('subheader'),
  role: z.literal(FieldRole.Subheader),
});

export const ValuePropPreheaderFieldSchema = RichTextFieldSchema.extend({
  name: z.literal('preheader'),
  role: z.literal(FieldRole.Preheader),
});

export const ValuePropSolutionDescriptionFieldSchema = RichTextFieldSchema.extend({
  name: z.literal('solutionDescription'),
  role: z.literal(FieldRole.Description),
});

export const ValuePropSolutionHeaderFieldSchema = RichTextFieldSchema.extend({
  name: z.literal('solutionHeader'),
  role: z.literal(FieldRole.Header),
});

export const ValuePropSolutionIconFieldSchema = ImageFieldSchema.extend({
  name: z.literal('solutionIcon'),
  role: z.literal(FieldRole.Image),
});

export const ValuePropBlockFieldSchema = z.discriminatedUnion('name', [
  ValuePropHeaderFieldSchema,
  ValuePropSubheaderFieldSchema,
  ValuePropPreheaderFieldSchema,
]);

export type ValuePropBlockField = z.infer<typeof ValuePropBlockFieldSchema>;

export const ValuePropCardFieldSchema = z.discriminatedUnion('name', [
  ValuePropSolutionDescriptionFieldSchema,
  ValuePropSolutionHeaderFieldSchema,
  ValuePropSolutionIconFieldSchema,
]);

export type ValuePropCardField = z.infer<typeof ValuePropCardFieldSchema>;

export const ValuePropBlockFieldGroupSchema = FieldGroupSchema.extend({
  fields: z.array(ValuePropBlockFieldSchema),
});

export type ValuePropBlockFieldGroup = z.infer<typeof ValuePropBlockFieldGroupSchema>;

export const ValuePropCardFieldGroupSchema = FieldGroupSchema.extend({
  fields: z.array(ValuePropCardFieldSchema),
});

export type ValuePropCardFieldGroup = z.infer<typeof ValuePropCardFieldGroupSchema>;

export const ValuePropStyleSchema = z.object({
  backgroundColor: z.string(),
});

export const ValuePropOptionsSchema = z.object({
  cardIconMode: CardIconModeSchema.optional(),
  cardStyle: CardStyleSchema.optional(),
  style: ValuePropStyleSchema.optional(),
  cardStyleEnabled: z.boolean().optional(),
  cardHeaderEnabled: z.boolean().optional(),
  preheaderEnabled: z.boolean().optional(),
  subheaderEnabled: z.boolean().optional(),
  entryCount: z.number().optional(),
  colorScheme: ColorSchemeSchema.optional(),
  layout: ValuePropLayoutSchema.optional(),
});
export type ValuePropOptions = z.infer<typeof ValuePropOptionsSchema>;

export const ValuePropBlockConfigSchema = z.object({
  options: ValuePropOptionsSchema,
  type: z.literal(BlockType.ValueProp),
  fieldGroups: z.tuple([ValuePropBlockFieldGroupSchema]).rest(ValuePropCardFieldGroupSchema),
  version: z.literal(2),
});

export type ValuePropBlockConfig = z.infer<typeof ValuePropBlockConfigSchema>;
